import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { ArrowBack } from '@mui/icons-material'
import { Alert, Box, Button, Card, CardContent, Checkbox, FormControlLabel, Grid, Skeleton, Stack, Typography } from '@mui/material'

import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog'
import { UserLanguage } from '../../../features/account/types/User'
import usePage from '../../../hooks/usePage'
import pageService from '../../../services/PageService'
import { ConfirmDialogData } from '../../../types/ConfirmDialogData'
import { useGetInternalAnalystsQuery } from '../../api/analysts'
import { useGetInternalGameDocumentQuery } from '../../api/games'
import {
  notStoredError,
  stillStoredError,
  useDeleteScreenshotCommentMutation,
  useGetInternalScreenshotCommentQuery,
  useGetInternalScreenshotsByIdsQuery,
  useSaveMultipleScreenshotCommentMutation,
  useSaveScreenshotCommentMutation,
} from '../../api/implementation-examples'
import { DiscardChangesDialog } from '../../components/DiscardChangesDialog/DiscardChangesDialog'
import { InputTranslator } from '../../components/InputTranslator/InputTranslator'
import { QueryStateBoundary } from '../../components/QueryStateBoundary/QueryStateBoundary'
import { CommentScreenshots } from '../../features/implementation-examples/components/CommentScreenshots/CommentScreenshots'
import {
  defaultImplementationMarketIso,
  getImplementationScreenshotsPath,
  parseScreenshotIds,
} from '../../features/implementation-examples/helpers/implementationExamplePaths'
import {
  getUnstoredMultipleCommentScreenshotIds,
  getUnstoredScreenshotCommentFields,
  hasEnglishText,
  screenshotCommentFieldLabelKeys,
  ScreenshotCommentFormValues,
  toMultipleScreenshotCommentMutation,
  toScreenshotCommentFormValues,
  toScreenshotCommentMutation,
} from '../../features/implementation-examples/helpers/screenshotCommentHelpers'
import { AnalystPicker } from '../../features/review/components/AnalystPicker/AnalystPicker'
import { PreviewLanguageSelect } from '../../features/review/components/PreviewLanguageSelect/PreviewLanguageSelect'
import { ReviewGameHeader } from '../../features/review/components/ReviewGameHeader/ReviewGameHeader'
import { ReviewPreview } from '../../features/review/components/ReviewPreview/ReviewPreview'
import { emptyTranslation, needsPublishConfirmation } from '../../features/review/helpers/reviewHelpers'
import { useEditPageLeave } from '../../hooks/useEditPageLeave'
import { useMutationFeedback } from '../../hooks/useMutationFeedback'
import { useScreenshotLinkEditorConfig } from '../../hooks/useScreenshotLinkEditorConfig'
import { InputTranslatorType } from '../../types/InputTranslatorType'

type PageAction = 'publish' | 'delete'

// No <form> here: the InputTranslator dialog has its own form, and React bubbles its submit through the portal into any parent form.
export const ScreenshotCommentEditPage: FC = () => {
  const { gameId = '' } = useParams<{ gameId: string }>()
  const [searchParams] = useSearchParams()
  const shotIdsParam = searchParams.get('shotIds')
  const screenshotIds = useMemo(() => parseScreenshotIds(shotIdsParam), [shotIdsParam])
  const marketIso = searchParams.get('marketIso') || defaultImplementationMarketIso
  const isMultiple = screenshotIds.length > 1
  usePage(pageService.getInternalPageWithId('internal-screenshot-comment-edit'))
  const { t } = useTranslation()
  const { run, notify } = useMutationFeedback()
  const navigate = useNavigate()
  const gameQuery = useGetInternalGameDocumentQuery(gameId, { skip: !gameId })
  const screenshotsQuery = useGetInternalScreenshotsByIdsQuery(screenshotIds, { skip: !screenshotIds.length, refetchOnMountOrArgChange: true })
  const screenshots = useMemo(
    () => screenshotIds.flatMap((id) => screenshotsQuery.data?.find((screenshot) => screenshot.id === id) ?? []),
    [screenshotIds, screenshotsQuery.data]
  )
  const screenshot = isMultiple ? undefined : screenshots[0]
  const storedCommentId = screenshot?.commentId ?? undefined
  const commentQuery = useGetInternalScreenshotCommentQuery(storedCommentId ?? '', { skip: !storedCommentId, refetchOnMountOrArgChange: true })
  const analystsQuery = useGetInternalAnalystsQuery()
  const [saveComment, { isLoading: isSaving }] = useSaveScreenshotCommentMutation()
  const [saveMultipleComment, { isLoading: isSavingMultiple }] = useSaveMultipleScreenshotCommentMutation()
  const [deleteComment, { isLoading: isDeleting }] = useDeleteScreenshotCommentMutation()
  const [previewLanguage, setPreviewLanguage] = useState<UserLanguage>('en')
  const [confirmDialogData, setConfirmDialogData] = useState<ConfirmDialogData<PageAction>>()
  const {
    control,
    reset,
    getValues,
    formState: { isDirty },
  } = useForm<ScreenshotCommentFormValues>({ defaultValues: toScreenshotCommentFormValues(undefined) })
  const values = useWatch({ control }) as ScreenshotCommentFormValues
  const { guard, leave } = useEditPageLeave(isDirty)
  const game = gameQuery.data
  const comment = storedCommentId ? commentQuery.data ?? undefined : undefined
  const isDangling = !!storedCommentId && commentQuery.isSuccess && !commentQuery.data
  const editorConfig = useScreenshotLinkEditorConfig(game?.id)
  const loadedCommentId = useRef<string>()
  const screenshotsPath = getImplementationScreenshotsPath(gameId, marketIso)

  // Only on the first load of a comment, so a refetch can't replace edits that weren't saved.
  useEffect(() => {
    if (comment && loadedCommentId.current !== comment.id) {
      loadedCommentId.current = comment.id
      reset(toScreenshotCommentFormValues(comment))
    }
  }, [comment, reset])

  const saveSingle = async () => {
    if (!game?.id || !screenshot) return
    const body = toScreenshotCommentMutation(comment, game.id, screenshot, getValues())
    try {
      const stored = await saveComment(body).unwrap()
      // Also on a mismatch, so loading the stored comment afterwards can't replace the edits kept for a retry.
      loadedCommentId.current = stored.comment.id
      const unstored = getUnstoredScreenshotCommentFields(body, stored.comment, stored.screenshot)
      if (unstored.length) {
        notify(
          t('internal-analysts:implementation_comment_not_stored_kept', {
            fields: unstored.map((field) => t(screenshotCommentFieldLabelKeys[field])).join(', '),
          }),
          'error'
        )
        return
      }
      reset(toScreenshotCommentFormValues(stored.comment))
      notify(t('internal-analysts:implementation_comment_saved'), 'success')
    } catch (error) {
      const notStored = (error as { error?: string } | undefined)?.error === notStoredError
      notify(t(notStored ? 'internal-analysts:implementation_comment_not_found_after_save' : 'internal-analysts:implementation_comment_save_failed'), 'error')
    }
  }

  const saveMultiple = async () => {
    if (!game?.id) return
    const body = toMultipleScreenshotCommentMutation(game.id, screenshotIds, getValues())
    const saved = await run(saveMultipleComment(body).unwrap(), { error: t('internal-analysts:implementation_comment_save_failed') })
    if (!saved.ok) return
    const unstoredIds = getUnstoredMultipleCommentScreenshotIds(body, saved.result.screenshots, saved.result.comments)
    if (unstoredIds.length) {
      notify(t('internal-analysts:implementation_comment_multiple_not_stored_kept', { count: unstoredIds.length, total: screenshotIds.length }), 'error')
      return
    }
    notify(t('internal-analysts:implementation_comment_saved_multiple', { count: screenshotIds.length }), 'success')
    leave(screenshotsPath)
  }

  const save = () => (isMultiple ? saveMultiple() : saveSingle())

  const remove = async () => {
    if (!comment) return
    try {
      const result = await deleteComment({ id: comment.id, screenshotId: screenshot?.id }).unwrap()
      if (result.screenshot?.commentId === comment.id) notify(t('internal-analysts:implementation_comment_deleted_still_linked'), 'warning')
      else notify(t('internal-analysts:implementation_comment_deleted'), 'success')
      leave(screenshotsPath)
    } catch (error) {
      const stillStored = (error as { error?: string } | undefined)?.error === stillStoredError
      notify(t(stillStored ? 'internal-analysts:implementation_comment_still_stored' : 'internal-analysts:implementation_comment_delete_failed'), 'error')
    }
  }

  const handleSave = () => {
    if (!needsPublishConfirmation(comment, getValues())) {
      save()
      return
    }
    setConfirmDialogData({
      title: t('internal-analysts:comment_publish_title'),
      confirmText: t(
        isMultiple ? 'internal-analysts:implementation_comment_publish_confirm_multiple' : 'internal-analysts:implementation_comment_publish_confirm',
        {
          name: game?.name,
          count: screenshotIds.length,
        }
      ),
      actionText: t('internal-analysts:review_publish'),
      data: 'publish',
    })
  }

  const handleDelete = () =>
    setConfirmDialogData({
      title: t('internal-analysts:comment_delete_title'),
      confirmText: t('internal-analysts:implementation_comment_delete_confirm', { name: game?.name, version: screenshot?.gameVersion ?? '---' }),
      actionText: t('internal-analysts:review_delete'),
      destructiveAction: true,
      data: 'delete',
    })

  const handleConfirm = (confirmed?: ConfirmDialogData<PageAction>) => {
    setConfirmDialogData(undefined)
    if (confirmed?.data === 'publish') save()
    if (confirmed?.data === 'delete') remove()
  }

  const isLoading = gameQuery.isLoading || screenshotsQuery.isLoading || (!!storedCommentId && commentQuery.isLoading)
  const missingCount = screenshotsQuery.data ? screenshotIds.length - screenshots.length : 0
  const loadError = !screenshotIds.length
    ? t('internal-analysts:implementation_comment_no_screenshots')
    : gameQuery.isError
    ? t('internal-analysts:review_game_load_failed')
    : screenshotsQuery.isError
    ? t('internal-analysts:implementation_screenshots_load_failed')
    : missingCount
    ? t('internal-analysts:implementation_comment_screenshots_missing', { count: missingCount })
    : screenshots.some((shot) => shot.gameId !== gameId)
    ? t('internal-analysts:implementation_comment_other_game')
    : commentQuery.isError
    ? t('internal-analysts:implementation_comment_load_failed')
    : comment && comment.screenshotId && comment.screenshotId !== screenshot?.id
    ? t('internal-analysts:implementation_comment_other_screenshot')
    : undefined
  const isBusy = isSaving || isSavingMultiple || isDeleting
  const canSave = hasEnglishText(values.comment)
  const analyst = analystsQuery.data?.find(({ id }) => id === values.analystId)
  const commentedCount = isMultiple ? screenshots.filter(({ commentId }) => !!commentId).length : 0

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button startIcon={<ArrowBack />} onClick={() => navigate(screenshotsPath)}>
            {t('internal-common:back')}
          </Button>
          <Typography variant="h5" fontWeight="bold">
            {t(isMultiple ? 'internal-analysts:implementation_comment_title_multiple' : 'internal-analysts:implementation_comment_title')}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          {comment && (
            <Button color="error" onClick={handleDelete} disabled={isLoading || !!loadError || isBusy}>
              {t('internal-common:delete')}
            </Button>
          )}
          <Button variant="contained" onClick={handleSave} disabled={isLoading || !!loadError || isBusy || !game?.id || !canSave}>
            {t('internal-common:save')}
          </Button>
        </Stack>
      </Stack>

      <QueryStateBoundary isLoading={isLoading} isError={!!loadError} errorText={loadError} skeletonHeight={360}>
        {game && (
          <Grid container spacing={3} wrap="wrap">
            <Grid item xs={12} md={7}>
              <Card>
                <CardContent>
                  <Stack spacing={3}>
                    <ReviewGameHeader
                      game={game}
                      detail={
                        isMultiple
                          ? t('internal-analysts:implementation_comment_screenshot_count', { count: screenshots.length })
                          : t('internal-analysts:comment_version', { version: screenshot?.gameVersion ?? '---' })
                      }
                    />
                    <CommentScreenshots screenshots={screenshots} />
                    {isMultiple && (
                      <Alert severity="warning">
                        <Typography variant="body2">{t('internal-analysts:implementation_comment_multiple_warning')}</Typography>
                        {commentedCount > 0 && (
                          <Typography variant="body2" mt={1}>
                            {t('internal-analysts:implementation_comment_multiple_replaces', { count: commentedCount, total: screenshots.length })}
                          </Typography>
                        )}
                      </Alert>
                    )}
                    {isDangling && <Alert severity="info">{t('internal-analysts:implementation_comment_dangling', { id: storedCommentId })}</Alert>}
                    <Controller
                      name="analystId"
                      control={control}
                      render={({ field }) => <AnalystPicker value={field.value} onChange={(id) => field.onChange(id)} disabled={isBusy} />}
                    />
                    {editorConfig ? (
                      <Controller
                        name="comment"
                        control={control}
                        render={({ field }) => (
                          <InputTranslator
                            value={field.value ?? emptyTranslation}
                            onChange={field.onChange}
                            inputType={InputTranslatorType.WYSIWYG}
                            label={t('internal-analysts:comment_comment')}
                            dialogTitle={t('internal-analysts:comment_comment')}
                            wysiwygConfig={editorConfig}
                            disabled={isBusy}
                          />
                        )}
                      />
                    ) : (
                      <Skeleton variant="rectangular" height={56} />
                    )}
                    {!canSave && (
                      <Typography variant="body2" color="text.secondary" mt={-2}>
                        {t('internal-analysts:implementation_comment_needs_english')}
                      </Typography>
                    )}
                    <Box>
                      <Controller
                        name="published"
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            control={<Checkbox checked={field.value} onChange={(event) => field.onChange(event.target.checked)} disabled={isBusy} />}
                            label={t('internal-analysts:review_published')}
                          />
                        )}
                      />
                      {comment?.published && (
                        <Typography variant="body2" color="text.secondary">
                          {t('internal-analysts:comment_published_help')}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box sx={{ position: 'sticky', top: (theme) => theme.spacing(2) }}>
                <ReviewPreview
                  title={t('internal-analysts:comment_preview_title')}
                  subtitle={t('internal-analysts:comment_preview_subtitle')}
                  analyst={analyst}
                  sections={[{ key: 'comment', html: values.comment?.[previewLanguage] }]}
                  actions={<PreviewLanguageSelect value={previewLanguage} onChange={setPreviewLanguage} />}
                />
              </Box>
            </Grid>
          </Grid>
        )}
      </QueryStateBoundary>

      <ConfirmDialog open={!!confirmDialogData} confirmDialogData={confirmDialogData} onClose={handleConfirm} />
      <DiscardChangesDialog
        guard={guard}
        title={t('internal-analysts:review_discard_title')}
        text={t('internal-analysts:review_discard_confirm')}
        discardText={t('internal-analysts:review_discard')}
      />
    </Box>
  )
}
